import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'
import { getDbOrThrow } from '@/lib/firebase'
import { mapPrice } from '@/lib/mappers'
import type { Currency, MaterialPrice } from '@/lib/types'

/**
 * Prices (`prices`): ONE document per material, with the material's id as the document id, so a
 * material can never have two prices.
 *
 * Visibility is inherited from the material and denormalised onto the price (`ownerId`, `isPrivate`):
 * a shared material has a shared price that admins maintain, a private material has a private price
 * only its owner sees. The rules verify both fields against the material document on every write, so
 * they cannot lie — and that denormalisation is what lets this read prices with the same two
 * provably-allowed queries the catalog uses.
 */
export function subscribeToPrices(
  uid: string,
  onData: (prices: MaterialPrice[]) => void,
  onError: (error: Error) => void,
): () => void {
  const db = getDbOrThrow()
  const owned = new Map<string, MaterialPrice>()
  const shared = new Map<string, MaterialPrice>()

  const push = () => {
    const merged = new Map(owned)
    for (const [id, item] of shared) merged.set(id, item)
    onData([...merged.values()])
  }

  const collect = (target: Map<string, MaterialPrice>) => (snapshot: {
    docs: Array<{ id: string; data: () => Record<string, unknown> }>
  }) => {
    target.clear()
    for (const item of snapshot.docs) {
      const price = mapPrice(item.id, item.data())
      if (price) target.set(price.materialId, price)
    }
    push()
  }

  const unsubscribeOwned = onSnapshot(
    query(collection(db, 'prices'), where('ownerId', '==', uid)),
    collect(owned),
    onError,
  )
  const unsubscribeShared = onSnapshot(
    query(collection(db, 'prices'), where('isPrivate', '==', false)),
    collect(shared),
    onError,
  )

  return () => {
    unsubscribeOwned()
    unsubscribeShared()
  }
}

/** Reads the price of one material, or null when none is set (or it is not visible to the caller). */
export async function getMaterialPrice(materialId: string): Promise<MaterialPrice | null> {
  try {
    const snapshot = await getDoc(doc(getDbOrThrow(), 'prices', materialId))
    return snapshot.exists() ? mapPrice(snapshot.id, snapshot.data()) : null
  } catch {
    // A price the caller may not read behaves like "no price".
    return null
  }
}

/**
 * Sets the price of a material. `ownerId`/`isPrivate` come from the material itself — the rules demand
 * they match, so they are never a UI choice. The whole document is written (no merge), which keeps the
 * field set exactly the one the rules whitelist.
 */
export function setMaterialPrice(
  material: { id: string; ownerId: string; isPrivate: boolean },
  amount: number,
  currency: Currency,
  actor: { uid: string; label: string },
): Promise<void> {
  return setDoc(doc(getDbOrThrow(), 'prices', material.id), {
    materialId: material.id,
    amount,
    currency,
    ownerId: material.ownerId,
    isPrivate: material.isPrivate,
    updatedBy: actor.uid,
    updatedByName: actor.label,
    updatedAt: serverTimestamp(),
  })
}

/** Clears a price ("no price set"), which is different from a price of zero. */
export function clearMaterialPrice(materialId: string): Promise<void> {
  return deleteDoc(doc(getDbOrThrow(), 'prices', materialId))
}
